/**
 * model-core 图片模型注册表
 *
 * 职责：
 * 1. 聚合 ./manifests/image 下各模型的独立配置（一模型一文件，互不共享）
 * 2. 提供按 model_id 查询配置；未注册模型返回兜底默认配置
 * 3. 派生查询：比例/清晰度/参考图上限/提示词上限/生成图尺寸约束等
 * 4. toImageFieldMap：把 config 转成与原接口 field_map 同构的形状，
 *    供现有组件（deleteFilter / ParameterPanel）无缝消费，避免大改组件逻辑
 *
 * 接入新模型：在 ./manifests/image 新增配置文件 → 加入 manifests/image/index.ts 的数组即可。
 */
import type { ConstraintRange, ImageModelConfig, ImageRatioOption, UploadConstraints } from './types';
import { IMAGE_MODEL_CONFIGS } from './manifests/image';

const configMap = new Map<string, ImageModelConfig>(IMAGE_MODEL_CONFIGS.map(c => [c.id, c]));

/** 未注册模型的兜底配置（对齐 feed 现有通用行为：全比例 / 无清晰度分档 / 参考图 16 张） */
export const DEFAULT_IMAGE_MODEL_CONFIG: ImageModelConfig = {
  id: '__default__',
  displayName: '',
  defaults: { ratio: '1:1' },
  ratioOptions: {
    '*': [
      { label: '1:1', value: '1:1' },
      { label: '2:3', value: '2:3' },
      { label: '3:2', value: '3:2' },
      { label: '3:4', value: '3:4' },
      { label: '4:3', value: '4:3' },
      { label: '9:16', value: '9:16' },
      { label: '16:9', value: '16:9' },
      { label: '21:9', value: '21:9' },
    ],
  },
  resolutionOptions: [],
  qualityOptions: [],
  toggles: {},
  limits: { maxRefImages: 16 },
};

/** 判断模型是否有前端独立配置 */
export const hasImageModelConfig = (id?: string | null): boolean =>
  !!id && configMap.has(id);

/** 获取模型配置；未注册时返回兜底默认配置 */
export const getImageModelConfig = (id?: string | null): ImageModelConfig =>
  (id && configMap.get(id)) || DEFAULT_IMAGE_MODEL_CONFIG;

/** 图片创作首屏默认模型候选（按 IMAGE_MODEL_CONFIGS 注册顺序作为优先级） */
export const DEFAULT_IMAGE_MODEL_IDS: string[] = IMAGE_MODEL_CONFIGS
  .filter(c => c.isDefault)
  .map(c => c.id);

/* ── 派生查询 ────────────────────────────────────────────────────────────────── */

/** 取指定清晰度档位下的比例选项；分档模型按 resolution 取组，不分档模型取 '*' 通用组 */
export const getRatioOptions = (
  config: ImageModelConfig,
  resolution?: string | null,
): ImageRatioOption[] =>
  config.ratioOptions[resolution ?? ''] ?? config.ratioOptions['*'] ?? [];

/** 该模型在指定清晰度下的比例选项是否都带真实像素宽高（决定面板是否显示宽高） */
export const hasPixelSize = (
  config: ImageModelConfig,
  resolution?: string | null,
): boolean => {
  const opts = getRatioOptions(config, resolution);
  return opts.length > 0 && opts.every(o => o.width !== undefined);
};

/** 参考图上限（未注册模型回落兜底配置的值） */
export const getModelImageLimit = (id?: string | null): number =>
  getImageModelConfig(id).limits.maxRefImages ?? 16;

/** 提示词字数上限；返回 undefined = 该模型未声明上限，面板不限字数 */
export const getModelPromptMaxLength = (id?: string | null): number | undefined =>
  getImageModelConfig(id).limits.promptMaxLength;

/** 参考图上传约束（未声明即不限制） */
export const getModelRefImageConstraints = (id?: string | null): UploadConstraints | undefined =>
  getImageModelConfig(id).limits.refImageConstraints;

/** 生成图尺寸约束（提交前校验；未声明即不校验） */
export const getModelGenerationConstraints = (
  id?: string | null,
): NonNullable<ImageModelConfig['limits']['generationConstraints']> | undefined =>
  getImageModelConfig(id).limits.generationConstraints;

/* ── field_map 派生（适配现有组件）───────────────────────────────────────────── */

/** 给选项补 select 标记，使其与原接口 field_map 的 options（带 select: true）同构 */
function markSelect<T extends { value: string | number }>(
  options: T[],
  defaultValue?: string | number,
): T[] {
  return options.map(o => (o.value === defaultValue ? { ...o, select: true } : o));
}

/**
 * 前端 config → 原接口 field_map 同构形状。
 * 现有组件（deleteFilter / ParameterPanel 的 mainFilter）读的都是 field_map 字段，
 * 此函数让它们可无缝切到 manifest 派生数据，无需改动组件内部的读取逻辑。
 *
 * 生成字段与真实 field_map 对齐：
 *   - `[清晰度].image_ratio.options`（分档模型）
 *   - `image_ratio.options`（顶层，不分档）
 *   - `resolution` / `quality` / `background`（含 label/default/options）
 *   - `stylize` / `change_degree` / `raw`（存在性标记）
 *   - `ref_type` / `ref_images`
 */
export function toImageFieldMap(config: ImageModelConfig): Record<string, any> {
  const fieldMap: Record<string, any> = {};

  // 清晰度分组 image_ratio（如 1K/2K/4K.image_ratio）
  for (const [key, options] of Object.entries(config.ratioOptions)) {
    if (key === '*') continue;
    fieldMap[key] = { image_ratio: { options: markSelect(options, config.defaults.ratio) } };
  }

  // 顶层 image_ratio（不分档）
  const starOptions = config.ratioOptions['*'];
  if (starOptions && starOptions.length > 0) {
    fieldMap.image_ratio = { options: markSelect(starOptions, config.defaults.ratio) };
  }

  // 清晰度
  if (config.resolutionOptions.length > 0) {
    fieldMap.resolution = {
      label: config.resolutionLabel,
      default: config.defaults.resolution,
      options: markSelect(config.resolutionOptions, config.defaults.resolution),
    };
  }

  // 图像质量（label 全模型固定为「图像质量」，与接口 field_map.quality.label 对齐）
  if (config.qualityOptions.length > 0) {
    fieldMap.quality = {
      label: '图像质量',
      default: config.defaults.quality,
      options: markSelect(config.qualityOptions, config.defaults.quality),
    };
  }

  // 背景（label 全模型固定为「背景」，与接口 field_map.background.label 对齐）
  if (config.backgroundOptions && config.backgroundOptions.length > 0) {
    fieldMap.background = {
      label: '背景',
      default: config.defaults.background,
      options: markSelect(config.backgroundOptions, config.defaults.background),
    };
  }

  // MJ 专属控件（存在性标记）
  if (config.toggles.stylize) fieldMap.stylize = {};
  if (config.toggles.changeDegree) fieldMap.change_degree = {};
  if (config.toggles.raw) fieldMap.raw = { value: config.defaults.raw };

  // 参考模式
  if (config.refTypeOptions && config.refTypeOptions.length > 0) {
    fieldMap.ref_type = {
      default: config.defaults.refType,
      options: markSelect(config.refTypeOptions, config.defaults.refType),
    };
  }

  // 参考图
  if (config.limits.maxRefImages != null) {
    fieldMap.ref_images = { max: config.limits.maxRefImages, min: 0 };
  }

  return fieldMap;
}
